/**
 * Module « boutique » — le parcours public d'achat (BOU-01…BOU-10, REZ-01…06).
 *
 * Monté sous `/boutique` par le routeur de la fondation, **sans garde** :
 * « aucun compte à créer pour acheter » (BOU-10). C'est le quatrième espace du
 * produit, à côté du back-office, du contrôle d'accès et du portail abonné.
 *
 *   /boutique                          la saison en cours, filtrable (BOU-01)
 *   /boutique/spectacles/:id           présentation, dates, tarifs   (BOU-02)
 *   /boutique/representations/:id      choix des places sur le plan  (BOU-03)
 *   /boutique/abonnements              formules et souscription      (BOU-05)
 *   /boutique/panier                   panier multi-représentations  (BOU-04)
 *   /boutique/coordonnees              identification de l'acheteur  (BOU-06)
 *   /boutique/recapitulatif            relecture avant de valider    (BOU-07)
 *   /boutique/paiement/:id             ★ confirmation — sur facture, sans
 *                                         étape de paiement (Lot P, 20/08) ;
 *                                         l'adresse et l'écran restent ceux
 *                                         qu'un futur prestataire en ligne
 *                                         réutiliserait tels quels (PAI-01)
 *
 * `LayoutBoutique` n'est PAS chargé paresseusement, contrairement aux pages :
 * c'est lui qui porte le compte à rebours (REZ-02), et un minuteur qui
 * apparaîtrait une demi-seconde après la page donnerait l'impression d'un temps
 * qui saute.
 */

import { lazy } from 'react'
import type { RouteObject } from 'react-router'

const PageBonsPublic = lazy(() =>
  import('../bons/public').then((m) => ({ default: m.PageBonsPublic })),
)

import { EcranSansObjet } from '../../components/EcranSansObjet'
import { CHEMINS } from '../../lib/chemins'

const PageAccueilBoutique = lazy(() =>
  import('./PageAccueilBoutique').then((m) => ({ default: m.PageAccueilBoutique })),
)
const PageSpectacle = lazy(() =>
  import('./PageSpectacle').then((m) => ({ default: m.PageSpectacle })),
)
const PageChoixPlaces = lazy(() =>
  import('./PageChoixPlaces').then((m) => ({ default: m.PageChoixPlaces })),
)
const PageAbonnements = lazy(() =>
  import('./PageAbonnements').then((m) => ({ default: m.PageAbonnements })),
)
const PagePanier = lazy(() => import('./PagePanier').then((m) => ({ default: m.PagePanier })))
const PageCoordonnees = lazy(() =>
  import('./PageCoordonnees').then((m) => ({ default: m.PageCoordonnees })),
)
const PageRecapitulatif = lazy(() =>
  import('./PageRecapitulatif').then((m) => ({ default: m.PageRecapitulatif })),
)
const PagePaiement = lazy(() => import('./PagePaiement').then((m) => ({ default: m.PagePaiement })))

const PageAttente = lazy(() =>
  import('./attente/PageAttente').then((m) => ({ default: m.PageAttente })),
)

const VerificationEmail = lazy(() =>
  import('../demandes/VerificationEmail').then((m) => ({ default: m.VerificationEmail })),
)
export const routesBoutique: RouteObject[] = [
  { path: 'verification-email', element: <VerificationEmail /> },
  { index: true, element: <PageAccueilBoutique /> },
  { path: 'attente', element: <PageAttente /> },
  { path: 'spectacles/:id', element: <PageSpectacle /> },
  { path: 'representations/:id', element: <PageChoixPlaces /> },
  /* ★ EVT-04 — la souscription d'une formule n'existe pas chez un tenant
     `evenement` : son provisionnement n'en crée aucune, et l'onglet est déjà
     absent de la coque (`LayoutBoutique`, `abonnementsDisponibles`). Ce renvoi
     ferme l'adresse tapée : un acheteur y lisait « le théâtre ne propose pas
     d'abonnement en ligne pour cette saison » — trois mots faux d'un coup, à
     quelqu'un venu prendre une entrée pour une fête d'entreprise. */
  {
    path: 'abonnements',
    element: (
      <EcranSansObjet vers={CHEMINS.boutique}>
        <PageAbonnements />
      </EcranSansObjet>
    ),
  },
  { path: 'bons', element: <PageBonsPublic /> },
  { path: 'panier', element: <PagePanier /> },
  { path: 'coordonnees', element: <PageCoordonnees /> },
  { path: 'recapitulatif', element: <PageRecapitulatif /> },
  /* ★ PAI-01 — l'adresse que le prestataire simulé rend comme `urlPaiement`,
     et celle qu'un vrai prestataire utilisera comme page de retour. */
  { path: 'paiement/:id', element: <PagePaiement /> },
]

export { LayoutBoutique } from './LayoutBoutique'
export { CompteARebours } from './CompteARebours'
export * from './hooks'
export * from './planPublic'
